import React, { useMemo, useState, useEffect, useRef } from "react";
import StandardModal from "../../../components/modales/StandardModal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { EmptyState, StatCard, type DataTableColumn } from "@routes-plan/ui";
import axios from "axios";
import moment from "moment-timezone";
import { formatCurrency } from '../../../utils/format/currency';
import { nowOperationalDateString, operationalTimezone } from '../../../utils/operationalDateTime';
import { useT } from '../../../i18n';
import { ModalTable, ReportStats } from '../components/ReportLayout';
interface ModalOfficeExpenseProps {
  open: boolean;
  onClose: () => void;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
  clientData?: {
    cashBoxId: number;
    startDate: string;
    endDate: string;
    routeName: string;
    routeId: string;
    typeOperation: number;
    isOutflow?: boolean;
  } | null;
}


const ConsolidatedExpensesModal: React.FC<ModalOfficeExpenseProps> = ({
  open,
  onClose,
  clientData,
  showAlert,
}) => {
  const t = useT();
  const [movement, setMovement] = useState<any[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  /** Evita re-ejecutar el efecto cuando App re-renderiza y recrea `showAlert` (no está memoizado). */
  const showAlertRef = useRef(showAlert);
  showAlertRef.current = showAlert;

  /** Clave estable: `clientData` como objeto en deps dispara efectos aunque los valores no cambien. */
  const clientKey = useMemo(() => {
    if (!clientData) return "";
    return [
      clientData.cashBoxId,
      clientData.startDate,
      clientData.endDate,
      String(clientData.routeId),
      clientData.typeOperation,
      clientData.isOutflow ?? false,
    ].join("|");
  }, [
    clientData?.cashBoxId,
    clientData?.startDate,
    clientData?.endDate,
    clientData?.routeId,
    clientData?.typeOperation,
    clientData?.isOutflow,
  ]);

  useEffect(() => {
    if (!open) {
      setMovement([]);
      setIsFetching(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !clientData || !clientKey) return;

    const ac = new AbortController();
    setIsFetching(true);
    setMovement([]);

    const fetchData = async () => {
      try {
        let records: any[] = [];
        const start = moment(clientData.startDate, ["YYYY-MM-DD", "YYYY/MM/DD"], true);
        const end = moment(clientData.endDate, ["YYYY-MM-DD", "YYYY/MM/DD"], true);
        const singleDay =
          start.isValid() && end.isValid() && start.isSame(end, "day");

        // Misma lógica que /reports/daily-summary (ModalOfficeExpense): gastos caja cobro
        // en un solo día vienen de cash_box_expense vía /cash-box-expenses/search.
        if (clientData.typeOperation === 3 && singleDay) {
          const numericRouteId = Number(clientData.routeId);
          if (!Number.isFinite(numericRouteId) || numericRouteId <= 0) {
            showAlertRef.current(t("summaryReports.consolidated.routeNotIdentified"), "warning");
            setMovement([]);
            return;
          }
          const requestDate = start.format("YYYY-MM-DD");
          const { data } = await axios.post(
            "/cash-box-expenses/search",
            { date: requestDate, routeId: numericRouteId },
            { signal: ac.signal }
          );
          if (Array.isArray(data)) {
            records = Array.isArray(data[0]) ? (data[0] ?? []) : data;
          } else if (Array.isArray(data?.data)) {
            records = data.data;
          }
        } else {
          const formData = {
            cashBoxId: clientData.cashBoxId,
            startDate: clientData.startDate,
            endDate: clientData.endDate,
            routeId: clientData.routeId,
            typeOperation: clientData.typeOperation,
            isOutflow: clientData.isOutflow ?? false,
          };
          const { data } = await axios.post("/reports/consolidated-day/expenses", formData, {
            signal: ac.signal,
          });
          records = Array.isArray(data?.data) ? data.data : [];
        }

        if (ac.signal.aborted) return;

        const filtered = records.filter(Boolean);
        setMovement(filtered);
        if (filtered.length === 0) {
          showAlertRef.current(t("summaryReports.noRecordsForQuery"), "info");
        } else {
          showAlertRef.current(
            filtered.length === 1
              ? t("summaryReports.recordFoundOne")
              : t("summaryReports.recordsFound", { count: filtered.length }),
            "success"
          );
        }
      } catch (error: unknown) {
        if (ac.signal.aborted) return;
        console.error("Error al cargar datos del modal:", error);
        showAlertRef.current(t("summaryReports.consolidated.expensesLoadError"), "error");
        setMovement([]);
      } finally {
        if (!ac.signal.aborted) {
          setIsFetching(false);
        }
      }
    };

    fetchData();

    return () => {
      ac.abort();
    };
    // `clientKey` fija el contenido lógico; `clientData` como objeto re-dispararía el efecto sin cambio real.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientKey]);

  const rows = useMemo(() => {
    return (Array.isArray(movement) ? movement : []).map((data, index) => {
      // Hora desde createdAt cuando existe (igual que en el resumen del día).
      const rawDateTime = data?.createdAt || data?.date || null;
      const dateMoment = rawDateTime ? moment.tz(rawDateTime, operationalTimezone()) : null;
      const fallbackDate = data?.date ? moment(data.date).format("YYYY/MM/DD") : "";
      return {
        id: String(data?.id ?? `${String(rawDateTime || '')}-${String(data?.description || '')}-${index}`),
        date: dateMoment?.isValid() ? dateMoment.format("YYYY/MM/DD") : fallbackDate,
        hour: dateMoment?.isValid() ? dateMoment.format("HH:mm") : "",
        value: Number(data?.value) || 0,
        type: String(data?.expenseType?.name || data?.type || ""),
        description: String(data?.description || ""),
      };
    });
  }, [movement]);

  const totalValue = rows.reduce((sum, item) => sum + (Number(item.value) || 0), 0);

  type MovementRow = (typeof rows)[number];
  const columns: DataTableColumn<MovementRow>[] = [
    { id: "date", header: t("common.date"), value: (r) => r.date, sortable: true },
    { id: "hour", header: t("summaryReports.col.hour"), value: (r) => r.hour, sortable: true },
    { id: "value", header: t("summaryReports.col.value"), value: (r) => r.value, render: (r) => formatCurrency(r.value), csv: (r) => formatCurrency(r.value), align: "right", sortable: true },
    { id: "type", header: t("summaryReports.col.type"), value: (r) => r.type, sortable: true },
    { id: "description", header: t("summaryReports.col.description"), value: (r) => r.description, wrap: true },
  ];

  return (
    <StandardModal
      open={open}
      onClose={onClose}
      title={t("summaryReports.consolidated.movementsTitle", {
        name: clientData?.typeOperation === 3 ? t("summaryReports.consolidated.theRoute") : clientData?.routeName || t("summaryReports.shared.route"),
      })}
      subtitle={t("summaryReports.dateRange", { start: clientData?.startDate ?? "", end: clientData?.endDate ?? "" })}
      loading={isFetching}
      maxWidth={1000}
      actions={
        <>
          <span />
          <Button variant="outlined" onClick={onClose}>
            {t("common.close")}
          </Button>
        </>
      }
    >
      {rows.length > 0 ? (
        <Box sx={{ display: "grid", gap: 2, minWidth: 0 }}>
          <ReportStats>
            <StatCard label={t("summaryReports.consolidated.movementCount")} value={rows.length} />
            <StatCard label={t("summaryReports.total")} value={formatCurrency(totalValue)} />
          </ReportStats>
          <ModalTable
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            exportFileName={t("summaryReports.consolidated.modalExportFile", { date: nowOperationalDateString() })}
            ariaLabel={t("summaryReports.consolidated.expensesTitle")}
          />
        </Box>
      ) : isFetching ? null : (
        <EmptyState dense title={t("summaryReports.noRecordsForQuery")} />
      )}
    </StandardModal>
  );
};

export default ConsolidatedExpensesModal;
